//------------------------------------------------------------------------------
// name: useProxyValue.ts
// author: mudas
// created: 2025/12/08
//------------------------------------------------------------------------------

import { useControllableValue } from 'ahooks';
import { useComputed } from 'common/hooks';

import { BaseOptionType, DefaultOptionType } from 'rc-select/es/Select';
import { Identity } from 'common/types';

export const useProxyValue = <
  ValueType = number | number[],
  OptionType extends BaseOptionType | DefaultOptionType = Identity,
>(props: {
  // 做为标识唯一值的字段名，默认为 id
  fieldName?: string;

  // 是否在 onChange 时返回 option 对象
  replaceAsOption?: boolean;

  // 同步 value 和 onChange，同样受 replaceAsOption 影响
  value?: ValueType | null;

  // 设置到 Select 组件的回调地，用于接收事件变化的数据
  onChange?: (value: ValueType) => void;
}) => {
  const { fieldName = 'id', replaceAsOption = false, ...otherProps } = props;
  const [proxyValue, setProxyValue] = useControllableValue<ValueType | OptionType | OptionType[] | undefined | null>(
    otherProps,
  );

  // 接收到的 value 转换成 proxyValue
  const onChange = (value: ValueType, option?: OptionType | OptionType[]) => {
    setProxyValue(replaceAsOption ? option : value);
  };

  // 将 proxyValue 转换成 value，以便 Select 组件使用
  const value = useComputed<ValueType>(() => {
    if (Array.isArray(proxyValue)) {
      return typeof proxyValue[0] === 'object'
        ? (proxyValue as OptionType[]).map((item) => item[fieldName])
        : (proxyValue as ValueType);
    } else {
      return typeof proxyValue === 'object' ? (proxyValue as OptionType)?.[fieldName] : proxyValue;
    }
  }, [proxyValue]);

  return { proxyValue, setProxyValue, value, onChange } as const;
};
